import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import AppShell from '../components/AppShell';

const UNITS = { piece:'шт', liter:'л', gram:'кг' };

// Текст одиниці з накладної → наша UnitType
function guessUnit(u) {
  const s = (u || '').toLowerCase();
  if (/л|liter|liters|літр/.test(s)) return 'liter';
  if (/кг|г\b|gram|грам|кіло/.test(s)) return 'gram';
  return 'piece';
}
const MOVE_TYPES = { income:'Прихід', write_off:'Списання', sale:'Продаж', inventory:'Інвентаризація' };
const MOVE_COLORS = { income:'#5fbf8f', write_off:'#ff6b6b', sale:'#6cb6ff', inventory:'#f5a623' };

export default function InventoryPage() {
  const { id: storeId } = useParams();
  const navigate = useNavigate();
  const { isStoreAdmin, hasAccessToStore } = useAuth();
  const [tab, setTab] = useState('income');
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supplier, setSupplier] = useState('');
  const [reason, setReason] = useState('');
  const [items, setItems] = useState([{ product_id:'', quantity:'', cost_price:'', note:'' }]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Сканування накладної
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState('');
  const [unmatched, setUnmatched] = useState([]);   // позиції, яких немає в каталозі
  const [supplierInfo, setSupplierInfo] = useState(null); // {name, id, matched} з розпізнавання
  const fileRef = useRef(null);

  const canEdit = isStoreAdmin(storeId);

  useEffect(() => {
    if (!hasAccessToStore(storeId)) { navigate('/dashboard'); return; }
    loadData();
  }, [storeId]);

  async function loadData() {
    setLoading(true);
    const [p, m] = await Promise.all([
      api.get(`/catalog/stores/${storeId}/products`).then(r => r.data).catch(() => []),
      api.get(`/inventory/stores/${storeId}/movements`).then(r => r.data).catch(() => []),
    ]);
    setProducts(p);
    setMovements(m);
    setLoading(false);
  }

  function addItem() {
    setItems([...items, { product_id:'', quantity:'', cost_price:'', note:'' }]);
  }

  function removeItem(i) {
    setItems(items.filter((_, idx) => idx !== i));
  }

  function updateItem(i, field, value) {
    setItems(items.map((item, idx) => idx === i ? {...item, [field]: value} : item));
  }

  function fillFromProduct(i, productId) {
    const product = products.find(p => p.id === productId);
    if (product) {
      updateItem(i, 'product_id', productId);
      if (!items[i].cost_price) updateItem(i, 'cost_price', product.cost_price);
    } else {
      updateItem(i, 'product_id', productId);
    }
  }

  // ── Сканування накладної ──
  function pickScanFile() {
    setScanMsg(''); setError(''); setSuccess('');
    if (fileRef.current) fileRef.current.click();
  }

  function onScanFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';   // дозволити повторний вибір того ж файлу
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => scanInvoice(String(reader.result), file.type);
    reader.readAsDataURL(file);
  }

  async function scanInvoice(dataUrl, mediaType) {
    setScanning(true); setScanMsg(''); setError(''); setUnmatched([]); setSupplierInfo(null);
    try {
      const res = await api.post('/inventory/scan-invoice', {
        store_id: storeId, image_base64: dataUrl, media_type: mediaType || 'image/jpeg',
      });
      const all = res.data.items || [];
      if (res.data.supplier) {
        setSupplier(res.data.supplier);
        setSupplierInfo({ name: res.data.supplier, id: res.data.supplier_id || null, matched: res.data.supplier_matched || null });
      }
      const matched = all.filter(it => it.product_id).map(it => ({
        product_id: it.product_id,
        quantity: String(it.quantity || ''),
        cost_price: it.price > 0 ? String(it.price) : '',
        note: '',
      }));
      const notFound = all.filter(it => !it.product_id);
      setItems(matched.length ? matched : [{ product_id:'', quantity:'', cost_price:'', note:'' }]);
      setUnmatched(notFound);
      setScanMsg(`Розпізнано позицій: ${all.length}. Знайдено в каталозі: ${matched.length}. Перевір і проведи прихід.`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Не вдалося розпізнати накладну');
    } finally {
      setScanning(false);
    }
  }

  // Створити товар з нерозпізнаної позиції і додати рядок приходу
  async function createFromUnmatched(idx) {
    const u = unmatched[idx];
    try {
      const res = await api.post(`/catalog/stores/${storeId}/products`, {
        name: u.name,
        price: u.price > 0 ? String(u.price) : '0',
        unit: guessUnit(u.unit),
        cost_price: u.price > 0 ? String(u.price) : '0',
        stock_quantity: '0',
      });
      setItems(prev => {
        const rows = prev.filter(r => r.product_id || r.quantity);
        return [...rows, { product_id: res.data.id, quantity: String(u.quantity || ''), cost_price: u.price > 0 ? String(u.price) : '', note: '' }];
      });
      setUnmatched(prev => prev.filter((_, i) => i !== idx));
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не вдалося створити товар');
    }
  }

  // Додати розпізнаного постачальника в довідник постачальників
  async function addSupplierToDirectory() {
    if (!supplierInfo?.name) return;
    try {
      const res = await api.post('/suppliers/', { name: supplierInfo.name });
      setSupplierInfo(s => ({ ...s, id: res.data.id, matched: res.data.name }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Не вдалося додати постачальника');
    }
  }

  async function submitIncome(e) {
    e.preventDefault();
    setError(''); setSuccess(''); setSubmitting(true);
    try {
      const payload = {
        store_id: storeId,
        supplier,
        items: items
          .filter(item => item.product_id && item.quantity)
          .map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            cost_price: item.cost_price || null,
            note: item.note || null,
          })),
      };
      if (!payload.items.length) throw new Error('Додайте хоча б один товар');
      const res = await api.post('/inventory/income', payload);
      setSuccess(`✅ Прийнято ${res.data.items.length} позицій`);
      setItems([{ product_id:'', quantity:'', cost_price:'', note:'' }]);
      setSupplier('');
      await loadData();
    } catch(err) {
      setError(err.response?.data?.detail || err.message || 'Помилка');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitWriteOff(e) {
    e.preventDefault();
    setError(''); setSuccess(''); setSubmitting(true);
    try {
      const payload = {
        store_id: storeId,
        reason,
        items: items
          .filter(item => item.product_id && item.quantity)
          .map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            note: item.note || null,
          })),
      };
      if (!payload.items.length) throw new Error('Додайте хоча б один товар');
      const res = await api.post('/inventory/write-off', payload);
      setSuccess(`✅ Списано ${res.data.items.length} позицій`);
      setItems([{ product_id:'', quantity:'', cost_price:'', note:'' }]);
      setReason('');
      await loadData();
    } catch(err) {
      setError(err.response?.data?.detail || err.message || 'Помилка');
    } finally {
      setSubmitting(false);
    }
  }

  function formatTime(iso) {
    return new Date(iso).toLocaleString('uk-UA', {
      day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit'
    });
  }

  const tabs = [
    ...(canEdit ? [
      { key:'income', label:'📥 Прихід' },
      { key:'writeoff', label:'🗑 Списання' },
    ] : []),
    { key:'movements', label:'📋 Історія' },
  ];

  return (
    <AppShell
      title="🛒 Складський облік"
      onBack={() => navigate(`/store/${storeId}`)}
    >
      <div className="tabbar">
        {tabs.map(t => (
          <button key={t.key} className={`tab ${tab===t.key ? 'active' : ''}`}
            onClick={() => { setTab(t.key); setError(''); setSuccess(''); }}>
            {t.label}
          </button>
        ))}
      </div>

      <div>
        {/* ── ПРИХІД ── */}
        {tab === 'income' && canEdit && (
          <div>
            <h3 style={styles.sectionTitle}>Прихід товару</h3>

            {/* Сканування накладної */}
            <input ref={fileRef} type="file" accept="image/*" capture="environment"
              style={{ display:'none' }} onChange={onScanFile} />
            <button type="button" style={styles.scanBtn} onClick={pickScanFile} disabled={scanning}>
              {scanning ? '🔎 Розпізнаю накладну…' : '📷 Сканувати накладну'}
            </button>
            {scanMsg && <p style={styles.scanMsg}>{scanMsg}</p>}

            <form onSubmit={submitIncome}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Постачальник (необов'язково)</label>
                <input style={styles.input} placeholder="Назва постачальника"
                  value={supplier} onChange={e => setSupplier(e.target.value)} />
                {supplierInfo && (
                  <div style={styles.supplierHint}>
                    {supplierInfo.id ? (
                      <span style={{ color:'#5fbf8f' }}>✓ Є в довіднику постачальників: {supplierInfo.matched}</span>
                    ) : (
                      <>
                        <span className="muted">Постачальника «{supplierInfo.name}» немає в довіднику.</span>
                        <button type="button" style={styles.addSupBtn} onClick={addSupplierToDirectory}>➕ Додати в постачальники</button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {unmatched.length > 0 && (
                <div style={styles.unmatchedBox}>
                  <div style={styles.unmatchedTitle}>⚠️ Немає в каталозі ({unmatched.length}) — створи або додай вручну:</div>
                  {unmatched.map((u, i) => (
                    <div key={i} style={styles.unmatchedRow}>
                      <span style={{ flex:1 }}>{u.name} <span className="muted">· {u.quantity} {u.unit} {u.price > 0 ? `· ${u.price} ₴` : ''}</span></span>
                      <button type="button" style={styles.createBtn} onClick={() => createFromUnmatched(i)}>➕ Створити та додати</button>
                      <button type="button" style={styles.skipBtn} onClick={() => setUnmatched(prev => prev.filter((_, idx) => idx !== i))}>Пропустити</button>
                    </div>
                  ))}
                </div>
              )}

              <div style={styles.itemsHeader}>
                <span style={styles.label}>ТОВАРИ</span>
                <button type="button" style={styles.addItemBtn} onClick={addItem}>+ Додати рядок</button>
              </div>

              {items.map((item, i) => {
                const product = products.find(p => p.id === item.product_id);
                return (
                  <div key={i} style={styles.itemRow}>
                    <select style={{...styles.input, flex:3}}
                      value={item.product_id}
                      onChange={e => fillFromProduct(i, e.target.value)} required>
                      <option value="">Оберіть товар...</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} (залишок: {parseFloat(p.stock_quantity).toFixed(2)} {UNITS[p.unit]})
                        </option>
                      ))}
                    </select>
                    <input style={{...styles.input, flex:1}} type="number" step="0.001" min="0.001"
                      placeholder={`Кількість ${product ? UNITS[product.unit] : ''}`}
                      value={item.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} required />
                    <input style={{...styles.input, flex:1}} type="number" step="0.01" min="0"
                      placeholder="Собівартість ₴"
                      value={item.cost_price} onChange={e => updateItem(i, 'cost_price', e.target.value)} />
                    <input style={{...styles.input, flex:2}} placeholder="Примітка"
                      value={item.note} onChange={e => updateItem(i, 'note', e.target.value)} />
                    {items.length > 1 && (
                      <button type="button" style={styles.removeBtn} onClick={() => removeItem(i)}>✕</button>
                    )}
                  </div>
                );
              })}

              {error && <p style={styles.error}>{error}</p>}
              {success && <p style={styles.success}>{success}</p>}

              <button type="submit" style={styles.submitBtn} disabled={submitting}>
                {submitting ? 'Збереження...' : '📥 Провести прихід'}
              </button>
            </form>
          </div>
        )}

        {/* ── СПИСАННЯ ── */}
        {tab === 'writeoff' && canEdit && (
          <div>
            <h3 style={styles.sectionTitle}>Списання товару</h3>
            <form onSubmit={submitWriteOff}>
              <div style={styles.fieldGroup}>
                <label style={styles.label}>Причина списання</label>
                <input style={styles.input} placeholder="Прострочено, бій, нестача..."
                  value={reason} onChange={e => setReason(e.target.value)} required />
              </div>

              <div style={styles.itemsHeader}>
                <span style={styles.label}>ТОВАРИ</span>
                <button type="button" style={styles.addItemBtn} onClick={addItem}>+ Додати рядок</button>
              </div>

              {items.map((item, i) => {
                const product = products.find(p => p.id === item.product_id);
                return (
                  <div key={i} style={styles.itemRow}>
                    <select style={{...styles.input, flex:3}}
                      value={item.product_id}
                      onChange={e => updateItem(i, 'product_id', e.target.value)} required>
                      <option value="">Оберіть товар...</option>
                      {products.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} (залишок: {parseFloat(p.stock_quantity).toFixed(2)} {UNITS[p.unit]})
                        </option>
                      ))}
                    </select>
                    <input style={{...styles.input, flex:1}} type="number" step="0.001" min="0.001"
                      placeholder={`Кількість ${product ? UNITS[product.unit] : ''}`}
                      value={item.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} required />
                    <input style={{...styles.input, flex:2}} placeholder="Примітка"
                      value={item.note} onChange={e => updateItem(i, 'note', e.target.value)} />
                    {items.length > 1 && (
                      <button type="button" style={styles.removeBtn} onClick={() => removeItem(i)}>✕</button>
                    )}
                  </div>
                );
              })}

              {error && <p style={styles.error}>{error}</p>}
              {success && <p style={styles.success}>{success}</p>}

              <button type="submit" style={{...styles.submitBtn, background:'rgba(255,107,107,.18)', color:'#ff6b6b'}} disabled={submitting}>
                {submitting ? 'Збереження...' : '🗑 Провести списання'}
              </button>
            </form>
          </div>
        )}

        {/* ── ІСТОРІЯ ── */}
        {tab === 'movements' && (
          <div>
            <h3 style={styles.sectionTitle}>Історія руху товарів</h3>
            {loading ? <p className="hint">Завантаження...</p> :
            movements.length === 0 ? <p className="hint">Рухів поки не було</p> : (
              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Товар</th>
                      <th style={{textAlign:'center'}}>Тип</th>
                      <th style={{textAlign:'right'}}>Кількість</th>
                      <th style={{textAlign:'right'}}>Залишок після</th>
                      <th>Примітка</th>
                      <th style={{textAlign:'right'}}>Час</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.map(m => (
                      <tr key={m.id}>
                        <td>{m.product_name}</td>
                        <td style={{textAlign:'center'}}>
                          <span style={{color: MOVE_COLORS[m.movement_type], fontSize:'12px', fontWeight:'bold', whiteSpace:'nowrap'}}>
                            {MOVE_TYPES[m.movement_type] || m.movement_type}
                          </span>
                        </td>
                        <td style={{textAlign:'right', whiteSpace:'nowrap', color: parseFloat(m.quantity) > 0 ? 'var(--green)' : 'var(--red)', fontWeight:700}}>
                          {parseFloat(m.quantity) > 0 ? '+' : ''}{parseFloat(m.quantity).toFixed(2)}
                        </td>
                        <td style={{textAlign:'right'}} className="muted">
                          {parseFloat(m.quantity_after).toFixed(2)}
                        </td>
                        <td className="muted" style={{fontSize:'13px'}}>{m.note || '—'}</td>
                        <td style={{textAlign:'right', whiteSpace:'nowrap', fontSize:'12px'}} className="muted">
                          {formatTime(m.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}

const styles = {
  sectionTitle: { color:'#f5a623', marginBottom:'20px' },
  fieldGroup: { marginBottom:'16px' },
  label: { display:'block', color:'#9a8a6e', fontSize:'12px', marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.5px' },
  input: { width:'100%', padding:'10px 14px', borderRadius:'8px', border:'1px solid #38301f', background:'#1c1812', color:'#fff', fontSize:'14px', boxSizing:'border-box' },
  itemsHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' },
  addItemBtn: { padding:'6px 16px', background:'#38301f', color:'#f5a623', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'13px' },
  itemRow: { display:'flex', gap:'8px', marginBottom:'10px', alignItems:'center', flexWrap:'wrap' },
  removeBtn: { padding:'8px 12px', background:'rgba(255,107,107,.18)', color:'#ff6b6b', border:'none', borderRadius:'6px', cursor:'pointer' },
  submitBtn: { marginTop:'20px', padding:'13px 32px', background:'#2d6a4f', color:'#5fbf8f', border:'none', borderRadius:'8px', fontSize:'15px', fontWeight:'bold', cursor:'pointer' },
  error: { color:'#ff6b6b', fontSize:'14px', margin:'12px 0' },
  success: { color:'#5fbf8f', fontSize:'14px', margin:'12px 0' },
  scanBtn: { width:'100%', padding:'12px', marginBottom:'10px', background:'#38301f', color:'#f5a623', border:'1px dashed #f5a623', borderRadius:'10px', cursor:'pointer', fontSize:'15px', fontWeight:'bold' },
  scanMsg: { color:'#9a8a6e', fontSize:'13px', margin:'2px 0 14px' },
  unmatchedBox: { background:'#211c14', border:'1px solid #4a3000', borderRadius:'10px', padding:'12px', marginBottom:'16px' },
  unmatchedTitle: { color:'#f5a623', fontSize:'13px', marginBottom:'10px' },
  unmatchedRow: { display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', padding:'6px 0', borderTop:'1px solid #38301f', fontSize:'14px' },
  createBtn: { padding:'6px 12px', background:'#2d6a4f', color:'#5fbf8f', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'13px', whiteSpace:'nowrap' },
  skipBtn: { padding:'6px 12px', background:'transparent', color:'#9a8a6e', border:'1px solid #38301f', borderRadius:'6px', cursor:'pointer', fontSize:'13px' },
  supplierHint: { display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap', marginTop:'6px', fontSize:'13px' },
  addSupBtn: { padding:'5px 12px', background:'#38301f', color:'#f5a623', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'12px' },
};
